import { lastDays, PRESETS, type DashboardWindow } from './dashboardWindow';

const FIELD_CLASS =
  'rounded-md border border-[var(--co-border)] bg-[var(--co-surface)] px-2 py-1.5 text-sm text-[var(--co-text)]';

type Props = {
  value: DashboardWindow;
  onChange: (window: DashboardWindow) => void;
  /** The last published day: presets end on it, and no later day is offered. */
  publishedThrough: string;
  problem: string | null;
};

export function DateRange({ value, onChange, publishedThrough, problem }: Props) {
  const activePreset = PRESETS.find((preset) => {
    const window = lastDays(preset.days, publishedThrough);
    return window.from === value.from && window.to === value.to;
  });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[var(--co-border)] bg-[var(--co-surface)] p-3">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Quick ranges">
          {PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              aria-pressed={activePreset?.key === preset.key}
              onClick={() => onChange(lastDays(preset.days, publishedThrough))}
              className={[
                'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                activePreset?.key === preset.key
                  ? 'bg-[var(--co-brand)] text-white'
                  : 'border border-[var(--co-border)] text-[var(--co-muted)] hover:text-[var(--co-text)]',
              ].join(' ')}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-[var(--co-muted)]">
          From
          <input
            type="date"
            aria-label="Window from"
            value={value.from}
            max={value.to}
            onChange={(event) => onChange({ ...value, from: event.target.value })}
            className={FIELD_CLASS}
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-[var(--co-muted)]">
          To
          <input
            type="date"
            aria-label="Window to"
            value={value.to}
            max={publishedThrough}
            onChange={(event) => onChange({ ...value, to: event.target.value })}
            className={FIELD_CLASS}
          />
        </label>
      </div>
      {problem && (
        <p role="alert" className="text-xs text-[var(--co-error)]">
          {problem}
        </p>
      )}
    </div>
  );
}
